import React, { useState, useEffect } from 'react'
import {
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifFormField,
  MotifFileUploader,
  MotifFileUploaderItem,
  MotifIcon,
  MotifModalFooter,
  MotifButton,
  MotifErrorMessage,
} from '@ey-xd/motif-react'
import { IconoirUpload } from '@ey-xd/motif-icon'
import { useParams } from 'react-router-dom'
import { formatBytes } from '@/utils/common'
import { fileExtensions } from '@/services/documents/documentsReducer'
import {
  useHRImportDocumentMutation,
  useLazyDeleteUploadedDocumentQuery,
  useLazyHRConfirmDocumentQuery,
} from '@/services/hrUpload/hrUploadReducer'
import './UploadDocument.scss'
import {
  addHRDocument,
  updateHRDocument,
} from '@/features/engagement/HRList/HRListSlice'
import { useDispatch } from 'react-redux'

type Props = {
  isVisible: boolean
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>
  setUploadDocId: React.Dispatch<React.SetStateAction<string>>
}

export default function UploadDocument({
  isVisible,
  setIsVisible,
  setUploadDocId,
}: Props) {
  const [file, setFile] = useState<File | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isError, setIsError] = useState<boolean | false>(false)
  const [isUploaded, setIsUploaded] = useState<boolean | false>(false)
  const [uploadDocumentId, setUploadDocumentId] = useState<string>('')
  const { engagementId } = useParams<{ engagementId: string }>()
  const dispatch = useDispatch()
  const [uploadDocument] = useHRImportDocumentMutation()
  const [deleteUploadDocument] = useLazyDeleteUploadedDocumentQuery()
  const [confirmDocument] = useLazyHRConfirmDocumentQuery()

  useEffect(() => {
    const uploadFile = async () => {
      if (file) {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('Name', file.name.replace(/\.[^/.]+$/, ''))
        formData.append('FileName', file.name)
        formData.append('CreatedBy', '01edadcb-fd67-4c66-aa2c-1a74d055fd16')
        try {
          const response = await uploadDocument({
            formData,
            engagementId,
          }).unwrap()
          setIsUploaded(true)
          setUploadDocumentId(response?.id)
        } catch (error: any) {
          console.log(error)
        } finally {
          setIsUploaded(true)
        }
      }
    }
    uploadFile()
  }, [file])
  const handleFileChange = async (files: any) => {
    if (files.length === 0) {
      return
    }
    if (files[0] && [...Object.keys(fileExtensions.excel), ...Object.keys(fileExtensions.csv)].includes(files[0].type)) {
      setFile(files[0])
      setIsError(false)
      setErrorMessage('')
    } else {
      setIsError(true)
      setErrorMessage('Your HR lists must be in CSV or Excel format.')
    }
  }
  const handleFileUploadCancel = async () => {
    if (uploadDocumentId !== '' && isUploaded) {
      deleteUploadDocument(uploadDocumentId).then((response: any) => {
        if (response.data) {
          setIsVisible(false)
          setFile(null)
          setIsUploaded(false)
          setUploadDocumentId('')
        } else {
          const errorMessage = response.error?.data?.message
          console.log(errorMessage)
        }
      })
    } else {
      setIsVisible(false)
      setFile(null)
      setIsUploaded(false)
      setErrorMessage('')
      setIsError(false)
    }
  }
  const handleConfirmUpload = async () => {
    const response = await confirmDocument(uploadDocumentId).unwrap()
    if (response) {
      //dispatch(updateHRDocument({ id: uploadDocumentId, status: true }))
      setUploadDocId(uploadDocumentId)
      setIsVisible(false)
      setFile(null)
      setIsUploaded(false)
      setUploadDocumentId('')
    }
  }
  return (
    <>
      <MotifModal
        onClose={() => handleFileUploadCancel()}
        show={isVisible}
        size="lg"
        focusTrapOptions={{
          tabbableOptions: {
            displayCheck: 'none',
          },
        }}
      >
        <MotifModalHeader className="no-border-bottom">
          <div className="motif-h6-default-regular">Upload HR User List</div>
        </MotifModalHeader>
        <MotifModalBody>
          <p>Please ensure the uploaded file contains all required details, including employee ID, email address, job title, and any other relevant information. The file must be clear, readable, and free from corruption or blurriness.</p>
          <MotifFormField>
            <MotifFileUploader
              id="hr-document-upload"
              label={'Click to upload or drag and drop '}
              labelDescription="Maximum file size : 100MB , File Format : CSV, Excel Single file only."
              onDrop={(files) => handleFileChange(files)}
              maxFileSize={10000}
              disabled={file !== null}
              accept={{ ...fileExtensions.excel, ...fileExtensions.csv }}
            >
              {file && (
                <MotifFileUploaderItem
                  fileIcon={<MotifIcon src={IconoirUpload} />}
                  fileName={file.name}
                  uploaded={isUploaded}
                  uploadedText={formatBytes(file.size)}
                  error={isError}
                  errorText={errorMessage || 'Invalid file format'}
                  isIndeterminate
                  percentUploaded={20}
                  showProgress={true}
                  className="file-uploader-item"
                />
              )}
            </MotifFileUploader>
            {isError && errorMessage && (
              <MotifErrorMessage>{errorMessage}</MotifErrorMessage>
            )}
          </MotifFormField>
        </MotifModalBody>
        <MotifModalFooter>
          <div className="motif-row justify-content-end gap-1">
            <MotifButton
              onClick={() => handleFileUploadCancel()}
              size="medium"
              type="button"
              variant="secondary"
            >
              Cancel
            </MotifButton>
            <MotifButton
              className=""
              onClick={() => handleConfirmUpload()}
              size="medium"
              type="button"
              disabled={uploadDocumentId === ''}
              variant="primary"
            >
              Confirm
            </MotifButton>
          </div>
        </MotifModalFooter>
      </MotifModal>
    </>
  )
}
